import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowUpRight, Clock } from '@untitled-ui/icons-react/build/cjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import LimitedAccessDeliveryModal from 'src/app/MyQuote/Modals/LimitedAccessDeliveryModal';
import { ILimitedAccessDeliveryData, ILimitedAccessDeliveryDataForm } from 'src/app/MyQuote/Modals/quoteDetailModal.interface';
import AutoCompleteGoogle from 'src/components/AutoCompleteGoogle/AutoCompleteGoogle';
import ButtonCmp from 'src/components/ButtonCmp';
import CommonModal from 'src/components/CommonModal';
import InputMaskCmp from 'src/components/InputMaskCmp';
import InputText from 'src/components/InputText/InputText';
import PhoneInput from 'src/components/PhoneInput/PhoneInput';
import Radio from 'src/components/Radio/Radio';
import useGoogleMaps from 'src/hooks/useGoogleMaps';
import { formatTimeInput, normalizeTimeValue } from 'src/utils/CommonFunctions';

import Address from '../../MyQuote/AddressDetails/Address';
import { AddressType, IAddressForm, IMapAddress, Service, addAddressSchema } from '../addressBook.const';
import { GroupedServices } from '../addressBook.helpers';
import ServiceCheckboxGroup from '../Components/ServiceCheckboxGroup';

import AdditionalServiceInfoModal from './AdditionalServiceInfoModal';

interface IProps {
    handleClose: (data?: IAddressForm) => void;
    onSubmit?: (data: IAddressForm, setError: (field: string, error: { type: string; message: string }) => void) => Promise<void> | void;
    additionalServices: GroupedServices;
    addressData: IAddressForm | null;
    setAddressData: (i: IMapAddress) => {};
}

const toHHMM = (val?: string | null): string => (val ? String(val).slice(0, 5) : '');

const AddAddressModal = ({ handleClose, onSubmit, additionalServices, addressData, setAddressData }: IProps) => {
    // constants
    const { t } = useTranslation();
    const [isShowAdditionalServiceInfoModal, setIsShowAdditionalServiceInfoModal] = useState<boolean>(false);
    const [isLimitedAccessDeliveryModalOpen, setIsLimitedAccessDeliveryModalOpen] = useState(false);
    const [limitedAccessDeliveryFormValues, setLimitedAccessDeliveryFormValues] = useState<ILimitedAccessDeliveryDataForm | null>(
        addressData?.limitedAccessType ? { limitedAccessType: addressData.limitedAccessType, limitedAccessOther: addressData.limitedAccessOther ?? '' } : null
    );

    const { isReady: isGoogleMapsScriptReady } = useGoogleMaps();
    const [isMapScript, setIsMapScript] = useState(false);
    const [isScriptLoading, setIsScriptLoading] = useState(false);
    const [selectedServiceInfo, setSelectedServiceInfo] = useState('');

    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);

    const [currentAddressData, setCurrentAddressData] = useState<IMapAddress | null>(addressData);

    const {
        control,
        handleSubmit,
        formState: { isSubmitted, isSubmitting, errors },
        watch,
        trigger,
        setValue,
        setError,
        clearErrors,
        getValues,
    } = useForm<any>({
        resolver: yupResolver(addAddressSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            ...(addressData || {
                id: undefined,
                addressType: AddressType.SHIPPER,
                companyName: '',
                contactName: '',
                contact: '',
                address: '',
                pickupAdditionalService: [],
                deliveryAdditionalService: [],
                fullAddress: '',
                city: '',
                state: '',
                country: '',
                postal: '',
                latitude: '',
                longitude: '',
                limitedAccessType: null,
                limitedAccessOther: null,
            }),
            businessHoursStartTime: toHHMM(addressData?.businessHoursStartTime),
            businessHoursEndTime: toHHMM(addressData?.businessHoursEndTime),
        },
    });

    const selectedAddressType = watch('addressType');
    const watchAddress = watch('address');
    const watchedValues = watch();
    const watchBusinessHoursStartTime = watch('businessHoursStartTime');
    const watchBusinessHoursEndTime = watch('businessHoursEndTime');

    const handleStartTimeChange = (e: any) => {
        setValue('businessHoursStartTime', formatTimeInput(e.target.value, watchBusinessHoursStartTime || ''));
        clearErrors('businessHoursStartTime');
    };

    const handleEndTimeChange = (e: any) => {
        setValue('businessHoursEndTime', formatTimeInput(e.target.value, watchBusinessHoursEndTime || ''));
        clearErrors('businessHoursEndTime');
    };

    const handleStartTimeBlur = () => {
        const normalized = normalizeTimeValue(watchBusinessHoursStartTime);

        if (normalized && normalized !== watchBusinessHoursStartTime) setValue('businessHoursStartTime', normalized);
    };

    const handleEndTimeBlur = () => {
        const normalized = normalizeTimeValue(watchBusinessHoursEndTime);

        if (normalized && normalized !== watchBusinessHoursEndTime) setValue('businessHoursEndTime', normalized);
    };

    const initialValuesRef = useRef<any>(null);

    useEffect(() => {
        if (addressData?.id) {
            initialValuesRef.current = getValues();
        }
    }, []);

    const hasChanges = useMemo(() => {
        if (!addressData?.id) return true;

        if (!initialValuesRef.current) return false;

        const keys = [
            'addressType',
            'companyName',
            'contactName',
            'contact',
            'address',
            'pickupAdditionalService',
            'deliveryAdditionalService',
            'businessHoursStartTime',
            'businessHoursEndTime',
        ] as const;

        const basicChanged = keys.some((key) => JSON.stringify(watchedValues[key]) !== JSON.stringify(initialValuesRef.current[key]));
        const normalize = (v: any) => (v == null || v === '' ? null : v);
        const limitedAccessChanged =
            normalize(limitedAccessDeliveryFormValues?.limitedAccessType) !== normalize(addressData?.limitedAccessType) ||
            normalize(limitedAccessDeliveryFormValues?.limitedAccessOther) !== normalize(addressData?.limitedAccessOther);

        return basicChanged || limitedAccessChanged;
    }, [watchedValues, addressData, limitedAccessDeliveryFormValues]);

    // An address is only "complete" when it has been fully resolved — i.e. the user selected
    // a specific result from the autocomplete (which populates city, postal, stateCode, lat, lng).
    // Country/city-level suggestions from Google don't populate postal, stateCode, or coordinates,
    // so they are treated as incomplete and the Confirm button stays disabled.
    // stateCode is required by the BE ("StateCode is not allowed to be empty").
    const isAddressComplete = useMemo(() => {
        if (!watchedValues.address) return true; // empty — let required validation handle it

        return !!(watchedValues.city && watchedValues.postal && watchedValues.postal.length >= 4 && watchedValues.latitude && watchedValues.longitude && watchedValues.stateCode);
    }, [watchedValues.address, watchedValues.city, watchedValues.postal, watchedValues.latitude, watchedValues.longitude, watchedValues.stateCode]);

    // Handles typed text in the address field (before selecting from dropdown).
    // Clears fullAddress and all geo fields so the form is invalid until the user selects
    // a real suggestion from the autocomplete dropdown — prevents a stale fullAddress from
    // making the Confirm button appear active for an unconfirmed typed address.
    const handleAddressInputChange = useCallback(
        (value: string) => {
            if (value === '') {
                setValue('address', '');
                setValue('fullAddress', '');
                setValue('city', '');
                setValue('state', '');
                setValue('country', '');
                setValue('postal', '');
                setValue('latitude', '');
                setValue('longitude', '');
                setValue('stateCode', '');
                setValue('countryCode', '');
                setValue('streetNumber', '');
                setCurrentAddressData(null);
                clearErrors('address');

                return;
            }

            setValue('address', value);
            setValue('fullAddress', '');
            setValue('city', '');
            setValue('state', '');
            setValue('country', '');
            setValue('postal', '');
            setValue('latitude', '');
            setValue('longitude', '');
            setValue('stateCode', '');
            setValue('countryCode', '');
            setValue('streetNumber', '');
            setCurrentAddressData(null);
            clearErrors('address');
        },
        [setValue, clearErrors]
    );

    // Handles address selection from the Google autocomplete dropdown
    const handleAutocompleteChange = useCallback(
        (value: any) => {
            if (!value) {
                if (!watchAddress) {
                    setValue('address', '');
                }

                return;
            }

            if (value.type === 'blur' || (value.target && value.type)) {
                if (!watchAddress) {
                    setValue('address', '');
                }

                return;
            }

            if (typeof value === 'object' && !value.target && (value.fullAddress === '' || (!value.fullAddress && !value.address1 && !value.city))) {
                setValue('address', '');

                return;
            }

            if (typeof value === 'object' && !value.target && value.fullAddress !== undefined) {
                const completeAddressObj: IMapAddress = {
                    fullAddress: value.fullAddress || '',
                    address1: value.address1 || '',
                    city: value.city || '',
                    state: value.state || '',
                    country: value.country || '',
                    postal: value.postal || '',
                    latitude: value.latitude || '',
                    longitude: value.longitude || '',
                    stateCode: value.stateCode || '',
                    countryCode: value.countryCode || '',
                    streetNumber: value.streetNumber || '',
                };

                setValue('address', completeAddressObj.fullAddress);
                setValue('fullAddress', completeAddressObj.fullAddress);
                setValue('city', completeAddressObj.city);
                setValue('state', completeAddressObj.state);
                setValue('country', completeAddressObj.country);
                setValue('postal', completeAddressObj.postal);
                setValue('latitude', completeAddressObj.latitude);
                setValue('longitude', completeAddressObj.longitude);
                setValue('address1', completeAddressObj.address1);
                setValue('stateCode', completeAddressObj.stateCode);
                setValue('countryCode', completeAddressObj.countryCode);
                setValue('streetNumber', completeAddressObj.streetNumber);

                setCurrentAddressData(completeAddressObj);
                setAddressData(completeAddressObj);
                clearErrors('address');
                trigger(['address']);
            }
        },
        [setValue, clearErrors, trigger, watchAddress, setAddressData]
    );

    // Handles data saved from the Address detail modal
    const handleAddressSubmit = useCallback(
        (data: IMapAddress) => {
            setCurrentAddressData(data);
            setAddressData(data);
            setValue('fullAddress', data.fullAddress);
            setValue('city', data.city);
            setValue('state', data.state);
            setValue('country', data.country);
            setValue('postal', data.postal);
            setValue('latitude', data.latitude);
            setValue('longitude', data.longitude);
            setValue('address', data.fullAddress);
            setValue('address1', data.address1);
            setValue('stateCode', data.stateCode);
            setValue('streetNumber', data.streetNumber);
            clearErrors('address');
            setIsAddressModalOpen(false);
        },
        [setValue, clearErrors, setAddressData]
    );

    // The shared hook owns loading the script. This used to assign window.initMap directly, which
    // clobbered any other component waiting on that single global callback.
    useEffect(() => {
        setIsMapScript(isGoogleMapsScriptReady);
        setIsScriptLoading(!isGoogleMapsScriptReady);
    }, [isGoogleMapsScriptReady]);

    const handleDeliveryServiceToggle = (service: Service, checked: boolean): boolean => {
        if (service.slug === 'limited_access_delivery') {
            if (checked) {
                setIsLimitedAccessDeliveryModalOpen(true);

                return true; // handled — do not add service ID yet
            } else {
                setLimitedAccessDeliveryFormValues(null);
                setValue('limitedAccessType', null);
                setValue('limitedAccessOther', null);

                return false; // let default remove the service ID
            }
        }

        return false;
    };

    const handleDeliveryEditService = (service: Service) => {
        if (service.slug === 'limited_access_delivery') {
            setIsLimitedAccessDeliveryModalOpen(true);
        }
    };

    const handleLimitedAccessSubmit = (data: ILimitedAccessDeliveryData) => {
        const formValues: ILimitedAccessDeliveryDataForm = {
            limitedAccessType: data.selectedLimitedAccessType,
            limitedAccessOther: data.limitedAccessOther,
        };

        setLimitedAccessDeliveryFormValues(formValues);
        setValue('limitedAccessType', data.selectedLimitedAccessType);
        setValue('limitedAccessOther', data.limitedAccessOther);

        const limitedAccessService = additionalServices.delivery.find((s) => s.slug === 'limited_access_delivery');

        if (limitedAccessService) {
            const currentDeliveryServices = getValues('deliveryAdditionalService') || [];

            if (!currentDeliveryServices.includes(limitedAccessService.id as any)) {
                setValue('deliveryAdditionalService', [...currentDeliveryServices, limitedAccessService.id as any]);
            }
        }

        setIsLimitedAccessDeliveryModalOpen(false);
    };

    const onFormSubmit = async (data: IAddressForm) => {
        if (!isAddressComplete) {
            setError('address', { type: 'manualWithTooltip', message: t('AddressBook.validation.Address_Incomplete') });

            return;
        }
        await onSubmit?.(data, setError);
    };

    return (
        <>
            <CommonModal
                title={addressData?.id ? t('AddressBook.Edit_Address') : t('AddressBook.Add_New_Address')}
                handleClose={() => handleClose()}
                size={'max-w-[688px]'}
                isOverflow={true}
                modalClassName=""
                primaryBtnText={addressData?.id ? t('Common.Save') : t('Common.Confirm')}
                primaryBtnDisabled={!hasChanges}
                primaryBtnLoading={isSubmitting}
                primaryBtnOnClick={handleSubmit(onFormSubmit)}
                secondaryBtnText={t('Common.Cancel')}
                secondaryBtnOnClick={() => handleClose()}
            >
                <form onSubmit={handleSubmit(onFormSubmit)} className="p-4 space-y-3">
                    <div className="flex flex-col">
                        <h6 className="form_label mb-2 block">{addressData?.id ? t('AddressBook.Edit_Address_Type') : t('AddressBook.Select_Address_Type')}</h6>
                        <Controller
                            name="addressType"
                            control={control}
                            render={({ field }) => (
                                <div className="flex gap-x-5 min-h-[18px] items-center">
                                    <Radio
                                        labelName={t('AddressBook.Shipper')}
                                        labelHtmlFor="shipper"
                                        id="shipper"
                                        checked={field.value === AddressType.SHIPPER}
                                        onChangeFunc={() => field.onChange(AddressType.SHIPPER)}
                                        radioLabelClassName="normal-case"
                                    />
                                    <Radio
                                        labelName={t('Common.Consignee')}
                                        labelHtmlFor="consignee"
                                        id="consignee"
                                        checked={field.value === AddressType.CONSIGNEE}
                                        onChangeFunc={() => field.onChange(AddressType.CONSIGNEE)}
                                        radioLabelClassName="normal-case"
                                    />
                                    <Radio
                                        labelName={t('Common.Both')}
                                        labelHtmlFor="both"
                                        id="both"
                                        checked={field.value === AddressType.BOTH}
                                        onChangeFunc={() => field.onChange(AddressType.BOTH)}
                                        radioLabelClassName="normal-case"
                                    />
                                </div>
                            )}
                        />
                    </div>
                    <div className="flex flex-col gap-3">
                        <Controller
                            name="companyName"
                            control={control}
                            render={({ field, fieldState }) => (
                                <InputText
                                    parentClassName="w-full"
                                    label={t('Common.Name')}
                                    size="medium"
                                    placeholder={t('Common.Enter_company_name')}
                                    labelClassName="block mb-1.5"
                                    required
                                    value={field.value}
                                    onChangeFunc={(e) => {
                                        const normalizedValue = e.target.value.replace(/\s+/g, ' ');
                                        field.onChange(normalizedValue);
                                    }}
                                    onBlur={() => {
                                        const trimmedValue = field.value?.trim() || '';
                                        field.onChange(trimmedValue);
                                        field.onBlur();
                                    }}
                                    isError={isSubmitted && !!errors.companyName}
                                    // Server-side rejections (the duplicate name+address guard) ride on the alert icon
                                    // inside the field, on hover. Required-field errors stay border-only per the house
                                    // rule, so the message is gated on the `server` error type rather than on any error.
                                    errorText={fieldState.error?.type === 'server' ? fieldState.error.message : undefined}
                                    isIconError={fieldState.error?.type === 'server'}
                                />
                            )}
                        />
                        <div className="grid grid-cols-2 gap-3">
                            <Controller
                                name="contactName"
                                control={control}
                                render={({ field }) => (
                                    <InputText
                                        parentClassName="flex-1"
                                        label={t('Common.Contact_Name')}
                                        placeholder={t('Common.Enter_contact_name')}
                                        labelClassName="block mb-1.5"
                                        required
                                        value={field.value}
                                        onChangeFunc={(e) => {
                                            const normalizedValue = e.target.value.replace(/\s+/g, ' ');
                                            field.onChange(normalizedValue);
                                        }}
                                        onBlur={() => {
                                            const trimmedValue = field.value?.trim() || '';
                                            field.onChange(trimmedValue);
                                            field.onBlur();
                                        }}
                                        isError={isSubmitted && !!errors.contactName}
                                    />
                                )}
                            />

                            <Controller
                                name="contact"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <div className="relative flex-1">
                                        <PhoneInput
                                            name={'contact'}
                                            label={t('Common.Phone_Number')}
                                            value={field.value}
                                            onNumberChange={(country: string, number: number, code: string) => {
                                                const newValue = `${code}${number}`;
                                                field.onChange(newValue);
                                                setValue('countryCode', country?.toUpperCase() || '');

                                                if (isSubmitted) {
                                                    trigger('contact');
                                                }
                                            }}
                                            required
                                            errors={errors.contact && isSubmitted ? true : false}
                                            isShowErrorMsg={!!(fieldState.error?.message && isSubmitted)}
                                        />
                                    </div>
                                )}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block mb-1.5 text-textSecondary text-xs font-medium">{t('Common.Business_Hours')}</label>
                        <div className="flex gap-x-3">
                            <Controller
                                name="businessHoursStartTime"
                                control={control}
                                render={({ field: { value }, fieldState: { error } }) => (
                                    <InputMaskCmp
                                        inputName="businessHoursStartTime"
                                        parentClassName="flex-1"
                                        placeholder={t('Common.Start_Time')}
                                        value={value || ''}
                                        HandleChange={handleStartTimeChange}
                                        handleBlur={handleStartTimeBlur}
                                        mask="99:99"
                                        icon={
                                            <div className="absolute top-1/2 -translate-y-1/2 right-3">
                                                <Clock className="h-4 w-4" />
                                            </div>
                                        }
                                        isError={!!error?.message}
                                    />
                                )}
                            />
                            <Controller
                                name="businessHoursEndTime"
                                control={control}
                                render={({ field: { value }, fieldState: { error } }) => (
                                    <InputMaskCmp
                                        inputName="businessHoursEndTime"
                                        parentClassName="flex-1"
                                        placeholder={t('Common.End_Time')}
                                        value={value || ''}
                                        HandleChange={handleEndTimeChange}
                                        handleBlur={handleEndTimeBlur}
                                        mask="99:99"
                                        icon={
                                            <div className="absolute top-1/2 -translate-y-1/2 right-3">
                                                <Clock className="h-4 w-4" />
                                            </div>
                                        }
                                        isError={!!error?.message}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    {isMapScript && (
                        <Controller
                            name="address"
                            control={control}
                            render={({ field, fieldState }) => (
                                <div className="flex items-end gap-x-2">
                                    <AutoCompleteGoogle
                                        inputName="address"
                                        mapParentClass="w-full"
                                        label={t('Common.Address')}
                                        placeholder={t('SetupAccount.OrganizationInfo.Organization_Address_Placeholder')}
                                        value={field.value}
                                        onChangeFunc={handleAutocompleteChange}
                                        onAddressInputChange={handleAddressInputChange}
                                        onBlur={handleAutocompleteChange}
                                        errorText={isSubmitted && (!!errors?.address || (!!watchedValues.address && !isAddressComplete))}
                                        errorTextTooltip={
                                            isSubmitted && watchedValues.address && !isAddressComplete
                                                ? t('AddressBook.validation.Address_Incomplete')
                                                : fieldState.error?.type === 'manualWithTooltip'
                                                ? fieldState.error?.message
                                                : null
                                        }
                                        required
                                        disabled={isScriptLoading}
                                        className="form_control truncate"
                                        isClearable
                                    />
                                    <ButtonCmp
                                        type="button"
                                        size="md"
                                        className="btn-outline-primary"
                                        icon={<ArrowUpRight className="w-4 h-4" />}
                                        onClick={() => setIsAddressModalOpen(true)}
                                        disabled={false}
                                    >
                                        Open
                                    </ButtonCmp>
                                </div>
                            )}
                        />
                    )}

                    {(selectedAddressType === AddressType.SHIPPER || selectedAddressType === AddressType.BOTH) && (
                        <ServiceCheckboxGroup
                            services={additionalServices.pickup}
                            name="pickupAdditionalService"
                            control={control}
                            title={t('AddressBook.Pickup_Additional_Services')}
                            showInfoIcon
                            onInfoClick={() => {
                                setSelectedServiceInfo('pickup');
                                setIsShowAdditionalServiceInfoModal(true);
                            }}
                        />
                    )}
                    {(selectedAddressType === AddressType.BOTH || selectedAddressType === AddressType.CONSIGNEE) && (
                        <ServiceCheckboxGroup
                            services={additionalServices.delivery}
                            name="deliveryAdditionalService"
                            control={control}
                            title={t('AddressBook.Delivery_Additional_Services')}
                            showInfoIcon
                            onInfoClick={() => {
                                setSelectedServiceInfo('delivery');
                                setIsShowAdditionalServiceInfoModal(true);
                            }}
                            onServiceToggle={handleDeliveryServiceToggle}
                            onEditService={handleDeliveryEditService}
                        />
                    )}
                </form>
            </CommonModal>
            {isShowAdditionalServiceInfoModal && (
                <AdditionalServiceInfoModal
                    title={selectedServiceInfo === 'pickup' ? 'Pickup Additional Services' : 'Delivery Additional Services'}
                    handleClose={() => setIsShowAdditionalServiceInfoModal(false)}
                    services={additionalServices?.[selectedServiceInfo as keyof GroupedServices].map((service: any) => ({
                        name: service?.name,
                        description: service?.description || '',
                    }))}
                />
            )}
            {isLimitedAccessDeliveryModalOpen && (
                <LimitedAccessDeliveryModal
                    handleClose={() => setIsLimitedAccessDeliveryModalOpen(false)}
                    handleLimitedAccess={handleLimitedAccessSubmit}
                    defaultFormValues={limitedAccessDeliveryFormValues}
                />
            )}
            <Address
                isOpen={isAddressModalOpen}
                mode={currentAddressData ? 'shipperAddress' : 'addressCreate'}
                handleClose={() => setIsAddressModalOpen(false)}
                setAddressData={handleAddressSubmit}
                addressData={currentAddressData}
                status={isMapScript}
                alertMessageShow={true}
            />
        </>
    );
};

export default AddAddressModal;
